import React from 'react';
import type { PreContractFillOrientation, PreContractIntakeForm } from '../lib/preContractTypes';
import { FILL_ORIENTATION_OPTIONS, parseFillOrientations } from '../lib/preContractTypes';

export type PreContractDesignConfigBarProps = {
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    /** `inline` = module layout toolbar; `popover` = AI designs config panel. */
    variant?: 'inline' | 'popover';
    /** Tighter grid for embedded module toolbar. */
    compact?: boolean;
    /** Inline labels + controls on one row (embedded solar popup). */
    dense?: boolean;
    className?: string;
};

export const PreContractDesignConfigBar: React.FC<PreContractDesignConfigBarProps> = ({
    form,
    patchForm,
    variant = 'inline',
    compact = false,
    dense = false,
    className = '',
}) => {
    const selectedFills = parseFillOrientations(form.fillOrientations);
    const toggleFillOrientation = (id: PreContractFillOrientation) => {
        const on = selectedFills.includes(id);
        patchForm({
            fillOrientations: on ? selectedFills.filter((item) => item !== id) : [...selectedFills, id],
        });
    };

    const shadeLabel = dense ? 'Shade' : 'Shaded panels';
    const orientLabel = dense ? 'Orient' : 'Orientation';

    return (
        <div
            className={`pci-config-bar${
                variant === 'inline' ? ' pci-config-bar--inline' : ' pci-config-bar--popover'
            }${compact ? ' pci-config-bar--compact-grid' : ''}${
                dense ? ' pci-config-bar--dense' : ''
            }${className ? ` ${className}` : ''}`}
            role="group"
            aria-label="Design configuration"
        >
            <div className="pci-config-bar__group">
                <span className="pci-config-bar__label">{shadeLabel}</span>
                <div className="pci-choice-group" role="group" aria-label="Shaded panels">
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact pci-choice--grey${
                            form.includeGreyPanels ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.includeGreyPanels}
                        title="Include grey panels (30–50% solar access)"
                        onClick={() => patchForm({ includeGreyPanels: !form.includeGreyPanels })}
                    >
                        Grey
                    </button>
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact pci-choice--purple${
                            form.includePurplePanels ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.includePurplePanels}
                        title="Include purple panels (<30% solar access)"
                        onClick={() => patchForm({ includePurplePanels: !form.includePurplePanels })}
                    >
                        Purple
                    </button>
                </div>
            </div>
            <div className="pci-config-bar__group">
                <span className="pci-config-bar__label">{orientLabel}</span>
                <div className="pci-choice-group" role="group" aria-label="Module orientation">
                    {FILL_ORIENTATION_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            className={`pci-choice pci-choice--compact${
                                selectedFills.includes(option.id) ? ' pci-choice--on' : ''
                            }`}
                            title={
                                option.hint +
                                ' (optional — leave both off to allow portrait and landscape)'
                            }
                            aria-pressed={selectedFills.includes(option.id)}
                            onClick={() => toggleFillOrientation(option.id)}
                        >
                            {dense
                                ? option.id === 'portrait'
                                    ? 'Port'
                                    : 'Land'
                                : option.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="pci-config-bar__group">
                <span className="pci-config-bar__label">Priority</span>
                <div className="pci-choice-group" role="group" aria-label="Design priority">
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact${
                            form.offsetPreference === 'full' ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.offsetPreference === 'full'}
                        title="Optional — prioritize covering annual usage"
                        onClick={() =>
                            patchForm({
                                offsetPreference: form.offsetPreference === 'full' ? null : 'full',
                            })
                        }
                    >
                        {dense ? 'Prod' : 'Production'}
                    </button>
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact${
                            form.offsetPreference === 'balanced' ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.offsetPreference === 'balanced'}
                        title="Optional — prioritize clean, high-SA arrays"
                        onClick={() =>
                            patchForm({
                                offsetPreference:
                                    form.offsetPreference === 'balanced' ? null : 'balanced',
                            })
                        }
                    >
                        {dense ? 'Aesth' : 'Aesthetics'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PreContractDesignConfigBar;
